import type { CSSProperties, ReactNode } from 'react';

import { CaretDoubleLeft, CaretDoubleRight, CaretLeft, CaretRight } from '@phosphor-icons/react';

import type { CustomAttribute } from '@/types';
import type { PaginateAttributes } from '@/ui/LoadMore';
import { cn } from '@/utils/cn';

interface NumberedPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  attributes: PaginateAttributes;
  customAttributes?: CustomAttribute;
  isLive?: boolean;
}

interface PaginationButtonProps {
  onClick: () => void;
  disabled: boolean;
  isActive?: boolean;
  children: ReactNode;
  className?: string;
  ariaLabel?: string;
  buttonStyles: ReturnType<typeof getButtonStyles>;
}

const PaginationButton = ({
  onClick,
  disabled,
  isActive = false,
  children,
  className,
  ariaLabel,
  buttonStyles,
}: PaginationButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    style={buttonStyles}
    className={cn(
      'flex items-center justify-center border transition duration-150 ease-in-out',
      !disabled && !isActive && 'hover:brightness-110',
      className,
    )}
    aria-label={ariaLabel}
  >
    {children}
  </button>
);

const getButtonStyles = (
  buttonBaseStyles: {
    borderRadius?: CSSProperties['borderRadius'];
    borderWidth?: CSSProperties['borderWidth'];
    borderStyle?: CSSProperties['borderStyle'];
    borderColor?: CSSProperties['borderColor'];
    boxShadow?: CSSProperties['boxShadow'];
  },
  attributes: PaginateAttributes,
  isActive: boolean,
  isDisabled: boolean,
) => ({
  ...buttonBaseStyles,
  backgroundColor: isActive ? attributes.paginateButtonTextColor : attributes.paginateButtonBackgroundColor,
  color: isActive ? attributes.paginateButtonBackgroundColor : attributes.paginateButtonTextColor,
  opacity: isDisabled ? 0.4 : 1,
  cursor: isDisabled ? 'not-allowed' : 'pointer',
  pointerEvents: isDisabled ? ('none' as const) : ('auto' as const),
});

const generatePageNumbers = (currentPage: number, totalPages: number): (number | 'ellipsis')[] => {
  const delta = 2; // Pages to show on each side of current page
  const range: number[] = [];
  const rangeWithDots: (number | 'ellipsis')[] = [];

  // Always include first page
  range.push(1);

  // Add pages around current page
  for (let i = currentPage - delta; i <= currentPage + delta; i++) {
    if (i > 1 && i < totalPages) {
      range.push(i);
    }
  }

  // Always include last page if more than 1 page
  if (totalPages > 1) {
    range.push(totalPages);
  }

  // Add ellipsis where there are gaps
  let prev = 0;
  for (const page of range) {
    if (page - prev === 2) {
      rangeWithDots.push(prev + 1);
    } else if (page - prev > 2) {
      rangeWithDots.push('ellipsis');
    }
    rangeWithDots.push(page);
    prev = page;
  }

  return rangeWithDots;
};

const NumberedPagination = ({
  currentPage,
  totalPages,
  onPageChange,
  attributes,
  customAttributes,
  isLive = false,
}: NumberedPaginationProps) => {
  if (totalPages <= 1 && isLive) {
    return null;
  }

  const pageNumbers = generatePageNumbers(currentPage, totalPages);

  const buttonBaseStyles = {
    borderRadius: attributes.paginateButtonBorderRadius,
    borderWidth: attributes.paginateButtonBorderWidth,
    borderStyle: attributes.paginateButtonBorderStyle,
    borderColor: attributes.paginateButtonBorderColor,
    boxShadow: attributes.paginateButtonShadow,
  };

  const handleClick = (page: number, isDisabled: boolean) => {
    if (!isDisabled) {
      onPageChange(page);
    }
  };

  return (
    <div
      className={cn(
        'col-span-12 mx-auto grid grid-cols-4 grid-rows-2 items-center gap-3 px-4 py-4 pb-8 sm:flex sm:justify-center',
        customAttributes?.className,
      )}
      style={{
        marginTop: attributes.paginationMarginTop,
        ...(customAttributes?.style || {}),
      }}
    >
      {/* Back buttons */}
      <div className="order-2 col-span-2 flex justify-end gap-2">
        <PaginationButton
          onClick={() => handleClick(1, currentPage === 1)}
          disabled={currentPage === 1}
          buttonStyles={getButtonStyles(buttonBaseStyles, attributes, false, currentPage === 1)}
          className="h-10 w-10"
          ariaLabel="First page"
        >
          <CaretDoubleLeft size={16} weight="bold" />
        </PaginationButton>
        <PaginationButton
          onClick={() => handleClick(currentPage - 1, currentPage === 1)}
          disabled={currentPage === 1}
          buttonStyles={getButtonStyles(buttonBaseStyles, attributes, false, currentPage === 1)}
          className="h-10 w-10"
          ariaLabel="Previous page"
        >
          <CaretLeft size={16} weight="bold" />
        </PaginationButton>
      </div>

      {/* Page numbers */}
      <div className="order-1 col-span-4 flex justify-center gap-3 sm:order-2">
        {pageNumbers.map((item, index) => {
          if (item === 'ellipsis') {
            // Use previous page number + 'ellipsis' for unique key
            const prevItem = index > 0 ? pageNumbers[index - 1] : 0;
            return (
              <span
                key={`ellipsis-after-${prevItem}`}
                className="flex h-10 min-w-[2.5rem] items-center justify-center px-3"
                style={{ color: attributes.paginateButtonTextColor }}
              >
                ...
              </span>
            );
          }

          const isActive = currentPage === item;
          return (
            <PaginationButton
              key={item}
              onClick={() => handleClick(item, false)}
              disabled={false}
              isActive={isActive}
              buttonStyles={getButtonStyles(buttonBaseStyles, attributes, isActive, false)}
              className="h-10 min-w-[2.5rem] px-3"
            >
              {item}
            </PaginationButton>
          );
        })}
      </div>

      {/* Forward buttons */}
      <div className="order-2 col-span-2 flex items-center gap-2">
        <PaginationButton
          onClick={() => handleClick(currentPage + 1, currentPage === totalPages)}
          disabled={currentPage === totalPages}
          buttonStyles={getButtonStyles(buttonBaseStyles, attributes, false, currentPage === totalPages)}
          className="h-10 w-10"
          ariaLabel="Next page"
        >
          <CaretRight size={16} weight="bold" />
        </PaginationButton>
        <PaginationButton
          onClick={() => handleClick(totalPages, currentPage === totalPages)}
          disabled={currentPage === totalPages}
          buttonStyles={getButtonStyles(buttonBaseStyles, attributes, false, currentPage === totalPages)}
          className="h-10 w-10"
          ariaLabel="Last page"
        >
          <CaretDoubleRight size={16} weight="bold" />
        </PaginationButton>
      </div>
    </div>
  );
};

export default NumberedPagination;
